import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SSCoinIcon, SSCoinBadge } from '../components/SSCoin';
import { toast } from 'sonner';
import { safePost } from '../utils/safeFetch';
import {
  ShoppingBag, CheckCircle, Loader2, Shield, DollarSign,
  TrendingUp, Coins, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COIN_RATE = 10;

export function SellerPaymentDialog({ product, userId, userCoinBalance, isRTL, onSuccess, onCancel }) {
  const [step, setStep] = useState('confirm');
  const [purchasing, setPurchasing] = useState(false);
  const navigate = useNavigate();

  const coinPrice = Math.round(product.buy_price * COIN_RATE);
  const hasEnough = (userCoinBalance || 0) >= coinPrice;

  const handlePurchaseWithCoins = async () => {
    if (!hasEnough) {
      toast.error(isRTL ? 'رصيد الكوينز غير كافٍ! قم بشراء المزيد.' : 'Not enough coins! Buy more.');
      return;
    }
    setPurchasing(true);
    try {
      const data = await safePost(`${API_URL}/api/store/add-product-coins`, {
        user_id: userId,
        product_id: product.id
      });
      setStep('success');
      setTimeout(() => onSuccess(data), 1500);
    } catch (e) {
      toast.error(e.message);
    }
    setPurchasing(false);
  };

  return (
    <AnimatePresence mode="wait">
      {/* CONFIRM */}
      {step === 'confirm' && (
        <motion.div key="confirm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="text-center mb-5">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center mx-auto mb-4 border border-amber-500/20">
              <SSCoinIcon size={48} />
            </div>
            <h3 className="font-heading text-xl font-bold text-foreground">{isRTL ? 'شراء المنتج بالكوينز' : 'Buy Product with Coins'}</h3>
            <p className="text-sm text-muted-foreground mt-1">{isRTL ? 'ادفع بكوينز S&S لإضافة المنتج لمتجرك' : 'Pay with S&S Coins to add to your store'}</p>
          </div>

          {/* Product */}
          <div className="flex items-center gap-4 p-4 rounded-2xl bg-muted/50 border border-border mb-5">
            <img src={product.image} alt="" className="w-16 h-16 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{isRTL ? product.name_ar : product.name}</p>
              <p className="text-xs text-muted-foreground">{isRTL ? product.category_name_ar : product.category_name}</p>
            </div>
          </div>

          {/* Price breakdown */}
          <div className="rounded-2xl border border-border overflow-hidden mb-5">
            <div className="flex items-center justify-between p-4 bg-amber-500/5">
              <div className="flex items-center gap-2">
                <SSCoinIcon size={20} />
                <span className="text-sm text-muted-foreground">{isRTL ? 'سعر المنتج:' : 'Product Price:'}</span>
              </div>
              <span className="font-mono text-xl font-bold text-amber-400">{coinPrice.toLocaleString()} Coins</span>
            </div>
            <div className="flex items-center justify-between p-4 border-t border-border">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[hsl(var(--success))]" />
                <span className="text-sm text-muted-foreground">{isRTL ? 'سعر البيع للعميل:' : 'Customer Price:'}</span>
              </div>
              <span className="font-mono text-xl font-bold text-[hsl(var(--success))]">${product.sell_price} USDT</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-primary/5 border-t border-primary/10">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">{isRTL ? 'ربحك المتوقع:' : 'Your Profit:'}</span>
              </div>
              <div className="text-right">
                <span className="font-mono text-xl font-bold text-primary">+${product.profit}</span>
                <Badge className="ml-2 bg-primary/10 text-primary border-0 text-xs">{product.profit_percent}%</Badge>
              </div>
            </div>
          </div>

          {/* Coin Balance */}
          <div className={`flex items-center justify-between p-4 rounded-xl border mb-5 ${hasEnough ? 'bg-[hsl(var(--success))]/5 border-[hsl(var(--success))]/20' : 'bg-destructive/5 border-destructive/20'}`}>
            <div>
              <p className="text-xs text-muted-foreground">{isRTL ? 'رصيدك الحالي:' : 'Your Balance:'}</p>
              <SSCoinBadge amount={userCoinBalance || 0} size="md" />
            </div>
            <div className="text-right">
              {hasEnough ? (
                <div className="flex items-center gap-1 text-[hsl(var(--success))]">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">{isRTL ? 'رصيد كافٍ' : 'Sufficient'}</span>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-1 text-destructive mb-1">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs font-medium">{isRTL ? 'رصيد غير كافٍ' : 'Insufficient'}</span>
                  </div>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { onCancel(); navigate('/buy-coins'); }}>
                    {isRTL ? 'شراء كوينز' : 'Buy Coins'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Trust */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground p-2.5 rounded-lg bg-[hsl(var(--success))]/5 border border-[hsl(var(--success))]/10 mb-5">
            <Shield className="w-4 h-4 text-[hsl(var(--success))] flex-shrink-0" />
            <span>{isRTL ? 'المنتج يُخزن في مستودعاتنا ونشحنه مباشرة لعميلك' : 'Product stored in our warehouse & shipped to your customer'}</span>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onCancel} className="flex-1 h-12 rounded-xl">{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button
              onClick={handlePurchaseWithCoins}
              disabled={purchasing || !hasEnough}
              className="flex-1 h-12 rounded-xl gap-2 text-base bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold shadow-lg shadow-amber-500/20"
              data-testid="seller-pay-button"
            >
              {purchasing ? <Loader2 className="w-5 h-5 animate-spin" /> : <SSCoinIcon size={22} />}
              {purchasing ? '...' : (isRTL ? `ادفع ${coinPrice} كوينز` : `Pay ${coinPrice} Coins`)}
            </Button>
          </div>
        </motion.div>
      )}

      {/* SUCCESS */}
      {step === 'success' && (
        <motion.div key="success" initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', damping: 15 }}>
          <div className="text-center py-8">
            <motion.div initial={{ scale: 0 }} animate={{ scale: [0, 1.3, 1] }} transition={{ duration: 0.6 }}>
              <div className="w-24 h-24 rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center mx-auto mb-4 border-2 border-[hsl(var(--success))]/20">
                <CheckCircle className="w-12 h-12 text-[hsl(var(--success))]" />
              </div>
            </motion.div>
            <h3 className="font-heading text-2xl font-bold text-foreground mb-2">{isRTL ? 'تمت الإضافة بنجاح!' : 'Added Successfully!'}</h3>
            <p className="text-muted-foreground">{isRTL ? 'المنتج أُضيف لمتجرك. شارك رابطك وابدأ البيع!' : 'Product added to your store. Share your link & sell!'}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
